import { useDashToast as useToast } from '@dashhudson/dashing-ui/toast';
import dayjs from 'dayjs';
import isEqual from 'lodash/isEqual';
import pick from 'lodash/pick';
import { defineStore } from 'pinia';
import { computed, ref, watch } from 'vue';
import { useRoute } from 'vue-router';

import * as VisionAiAPI from '@/apis/vision-ai';
import { campaignRouteNames } from '@/app/campaigns/constants';
import { useCommunityVisionAi } from '@/app/community/composables/useCommunityVisionAi';
import { COMMUNITY_ROUTE_NAMES } from '@/app/community/constants';
import { communityRouteName } from '@/app/community/routes';
import { DASHBOARD_ROUTE_NAMES } from '@/app/dashboards/constants';
import { SOCIAL_LISTENING_ROUTE_NAMES } from '@/app/socialListening/constants';
import { useIntentMap } from '@/components/VisionAi/composables/useIntentMap';
import { useVisionAiPermissions } from '@/components/VisionAi/composables/useVisionAiPermissions';
import { useVisionAiSourceLinks } from '@/components/VisionAi/composables/useVisionAiSourceLinks';
import {
  ACTION_TYPES,
  BACKUP_PROMPT_MESSAGE,
  DEFAULT_METRIC_AND_ANALYTICS_PROMPTS,
  DEFAULT_SUMMARY_PROMPTS_BY_INTENT_TYPE,
  INTENT_TYPES,
  INTENT_TYPES_BY_ROUTE_NAME,
  REQUEST_CONTEXT_BUTTON_IDENTIFIERS,
  SUMMARY_INTENT_TYPES,
  VISION_AI_MIXPANEL_EVENTS,
  VISION_AI_RESOURCE_CENTER_URL,
  VISION_AI_SOCKET_EVENTS,
  VISION_AI_WIDGET_INTERACTIONS,
  VISION_AI_WIDGET_LAUNCH_INTERACTIONS,
} from '@/components/VisionAi/constants';
import { getIntentQuery } from '@/components/VisionAi/utils/intent_query';
import { widgetLaunchEvent } from '@/components/VisionAi/utils/mixpanel-events';
import { buildRequestContext } from '@/components/VisionAi/utils/request-context';
import { VISION_AI_SENDER } from '@/config';
import { useCampaignsStore } from '@/stores/campaigns';
import { useDashboardsStore } from '@/stores/dashboards';
import { useFlagStore } from '@/stores/flag';
import { useIdentityStore } from '@/stores/identity';
import { useNotificationStore } from '@/stores/notification';
import { useSocketStore } from '@/stores/socket';
import { useTrackingStore } from '@/stores/tracking';
import { logger } from '@/utils/logger';

import { useVisionAiConversationsStore } from './vision-ai-conversations';
import { useVisionAiMessagesStore } from './vision-ai-messages';

export const useVisionAiStore = defineStore('visionAi', () => {
  const socketStore = useSocketStore();
  const campaignsStore = useCampaignsStore();
  const dashboardsStore = useDashboardsStore();
  const identityStore = useIdentityStore();
  const flagStore = useFlagStore();
  const notificationStore = useNotificationStore();
  const visionAiConversationsStore = useVisionAiConversationsStore();
  const visionAiMessagesStore = useVisionAiMessagesStore();
  const { getWarningMessage } = useIntentMap();
  const { addToast: addToastWithLife } = useToast({ life: 5000 });
  const { addToast: addPersistentToast } = useToast();

  const route = useRoute();
  const communityVisionAi = useCommunityVisionAi();

  // refs
  const visionAiChatWindowOpen = ref(false);
  const forceHideVisionAi = ref(false);
  const currentSourceUrl = ref(null);
  const popupIsExpanded = ref(false);
  const showConversationHistorySidebar = ref(false);
  // Use to suppress default empty state if first user message will be preset, like with summaries
  const expectingNewMessage = ref(false);

  // Vision AI side panel open state, synced from VisionChatSidePanel via the dashing-ui
  // side panel composable. Used by the task-completion toast trigger to suppress the
  // toast when the user is already viewing the chat.
  const isVisionChatPanelOpen = ref(false);

  // Task-completion toast state. When a UNIFIED assistant message arrives while the
  // chat panel is closed, the floating Vision button expands into a pill for 5s, then
  // collapses back to the icon while retaining a pointer to the first-finished
  // conversation until the user clicks.
  const PENDING_COMPLETION_PILL_MS = 5000;
  const pendingCompletionConversationId = ref(null);
  const isPendingCompletionPillExpanded = ref(false);
  let pendingCompletionTimerId = null;

  // Persistent "unread" state. A conversation enters the set when a UNIFIED assistant
  // message arrives for a conversation the user isn't actively viewing, and leaves it
  // when the user views it (panel open + that conversation selected). Independent of
  // the 5s pill above — the badge stays until read.
  const unreadConversationIds = ref(new Set());

  // Loading-pill state. Triggered when the chat panel closes while tasks are still
  // in flight. Expanded for 5s, then collapses to the icon-only spinner while the
  // task continues.
  const LOADING_PILL_MS = 5000;
  const isLoadingPillExpanded = ref(false);
  let loadingPillTimerId = null;

  const {
    userHasVisionAiPermissionForCurrentRoute,
    someBrandCanAccessDashboardsAndVisionAi,
    someUserBrandCanAccessVisionAiConversational,
    userHasVisionAiButNotConversational: canAccessVisionAiButNotConversational,
  } = useVisionAiPermissions();

  const pending = ref({
    conversation: false,
    messages: false,
    regenerateMessage: false,
  });

  const messages = ref([]);

  // computed

  const canAccessConversational = computed(
    () =>
      someBrandCanAccessDashboardsAndVisionAi.value &&
      someUserBrandCanAccessVisionAiConversational.value,
  );

  const canAccessVisionAiTextbox = computed(
    () => canAccessConversational.value || canAccessVisionAiButNotConversational.value,
  );

  function expandChatPopup() {
    popupIsExpanded.value = true;
    showConversationHistorySidebar.value = true;
  }

  function shrinkChatPopup() {
    popupIsExpanded.value = false;
    showConversationHistorySidebar.value = false;
  }

  function hideConversationHistory() {
    showConversationHistorySidebar.value = false;
  }

  const promptConfigForRoute = computed(() => {
    if (route?.name?.startsWith(communityRouteName)) {
      return communityVisionAi.defaultSummarizePromptConfig.value;
    }

    if (route?.name === SOCIAL_LISTENING_ROUTE_NAMES.OVERVIEW) {
      return { message: BACKUP_PROMPT_MESSAGE };
    }

    if (
      route?.name === campaignRouteNames.CAMPAIGN_DETAIL_OWNED_SUMMARY &&
      flagStore.ready &&
      flagStore.flags.campaignCommentSummaries
    ) {
      return {
        message: 'Summarize Campaign Comments',
        intentTypeOverride: INTENT_TYPES.UNIFIED,
        buttonIdentifier: REQUEST_CONTEXT_BUTTON_IDENTIFIERS.SUMMARIZE_CAMPAIGN_COMMENTS,
        extra: { campaign_id: campaignsStore.campaign.id },
      };
    }

    const intent = INTENT_TYPES_BY_ROUTE_NAME?.[route?.name];
    const message = DEFAULT_SUMMARY_PROMPTS_BY_INTENT_TYPE?.[intent];
    return message ? { message } : null;
  });

  const defaultPrompts = computed(() => {
    let prompts = [];

    if (route && userHasVisionAiPermissionForCurrentRoute.value && promptConfigForRoute.value) {
      prompts.push(promptConfigForRoute.value);
    }
    if (canAccessConversational.value) {
      prompts = prompts.concat(
        DEFAULT_METRIC_AND_ANALYTICS_PROMPTS.map((message) => ({ message })),
      );
    }

    return prompts;
  });

  // Canned chip for the Vision AI side panel: only the route Summarize prompt from `promptConfigForRoute`.
  const sidePanelCannedPrompts = computed(() => {
    if (!route || !userHasVisionAiPermissionForCurrentRoute.value || !promptConfigForRoute.value) {
      return [];
    }
    const summarize = promptConfigForRoute.value;
    return [
      {
        label: summarize.label || summarize.message,
        promptText: summarize.message,
        visionPromptConfig: summarize,
      },
    ];
  });

  const loadingMessageInCurrentConversation = computed(() => {
    if (visionAiConversationsStore.summariesSelected) {
      return visionAiMessagesStore.summaryLoading;
    }
    return visionAiConversationsStore.isMessageLoadingInCurrentConversation;
  });

  const isCommunityInsightsRoute = computed(() => route?.name === COMMUNITY_ROUTE_NAMES.INSIGHTS);

  const currentIntentType = computed(() => {
    if (route?.name?.startsWith(communityRouteName)) {
      return communityVisionAi.communityIntentType.value;
    }
    return INTENT_TYPES_BY_ROUTE_NAME?.[route?.name];
  });

  function clearLocalChatHistory() {
    visionAiMessagesStore.setMessages({});
  }

  async function clearChatHistory() {
    pending.value.messages = true;
    let response;
    try {
      response = await VisionAiAPI.clearMessages();
      visionAiMessagesStore.setMessages({});
      visionAiConversationsStore.setCurrentConversationId(null);
      visionAiMessagesStore.anySummaryLoaded = false;
      clearAllUnread();
    } catch (error) {
      logger.error('Failed to clear vision ai messages', {}, error);
      throw error;
    } finally {
      pending.value.messages = false;
    }

    return response ?? {};
  }

  function openChatWindow() {
    if (visionAiChatWindowOpen.value) return;

    visionAiChatWindowOpen.value = true;
    expandChatPopup();
    widgetLaunchEvent({
      interaction: VISION_AI_WIDGET_LAUNCH_INTERACTIONS.OPEN,
      path: route?.path,
    });
  }

  function closeChatWindow() {
    if (!visionAiChatWindowOpen.value) return;
    visionAiChatWindowOpen.value = false;
    widgetLaunchEvent({
      interaction: VISION_AI_WIDGET_LAUNCH_INTERACTIONS.CLOSE,
      path: route?.path,
    });
  }

  const hasPendingCompletion = computed(() => pendingCompletionConversationId.value !== null);

  function clearPendingCompletion() {
    pendingCompletionConversationId.value = null;
    isPendingCompletionPillExpanded.value = false;
    if (pendingCompletionTimerId !== null) {
      clearTimeout(pendingCompletionTimerId);
      pendingCompletionTimerId = null;
    }
  }

  // While the pill is still expanded the pointer stays on the first finished task —
  // a second completion arriving inside the 5s window is ignored. Once the pill has
  // collapsed, the next completion re-expands it and updates the pointer to the
  // latest finished task.
  function triggerPendingCompletion(conversationId) {
    if (isPendingCompletionPillExpanded.value) return;
    // Insight Pill replaces an active Loading Pill — once Insight expires, the
    // button collapses straight to the spinner, not back into the loading copy.
    clearLoadingPill();
    if (pendingCompletionTimerId !== null) clearTimeout(pendingCompletionTimerId);
    pendingCompletionConversationId.value = conversationId;
    isPendingCompletionPillExpanded.value = true;
    pendingCompletionTimerId = setTimeout(() => {
      isPendingCompletionPillExpanded.value = false;
      pendingCompletionTimerId = null;
    }, PENDING_COMPLETION_PILL_MS);
  }

  function consumePendingCompletion() {
    const conversationId = pendingCompletionConversationId.value;
    clearPendingCompletion();
    return conversationId;
  }

  function clearLoadingPill() {
    isLoadingPillExpanded.value = false;
    if (loadingPillTimerId !== null) {
      clearTimeout(loadingPillTimerId);
      loadingPillTimerId = null;
    }
  }

  function triggerLoadingPill() {
    if (isVisionChatPanelOpen.value) return;
    if (loadingPillTimerId !== null) clearTimeout(loadingPillTimerId);
    isLoadingPillExpanded.value = true;
    loadingPillTimerId = setTimeout(() => {
      isLoadingPillExpanded.value = false;
      loadingPillTimerId = null;
    }, LOADING_PILL_MS);
  }

  function setVisionChatPanelOpen(open) {
    const wasOpen = isVisionChatPanelOpen.value;
    isVisionChatPanelOpen.value = open;
    if (open) {
      clearPendingCompletion();
      clearLoadingPill();
    } else if (wasOpen && visionAiConversationsStore.loadingConversationIds.length > 0) {
      // Panel just closed with tasks still in flight — show the loading pill for 5s
      // before it collapses back to the icon-only spinner.
      triggerLoadingPill();
    }
  }

  const hasUnreadConversations = computed(() => unreadConversationIds.value.size > 0);

  function isConversationUnread(conversationId) {
    return unreadConversationIds.value.has(conversationId);
  }

  function markConversationUnread(conversationId) {
    if (conversationId == null) return;
    // User is actively viewing this conversation — skip.
    if (
      isVisionChatPanelOpen.value &&
      conversationId === visionAiConversationsStore.currentConversationId
    ) {
      return;
    }
    if (unreadConversationIds.value.has(conversationId)) return;
    const next = new Set(unreadConversationIds.value);
    next.add(conversationId);
    unreadConversationIds.value = next;
  }

  function markConversationRead(conversationId) {
    if (!unreadConversationIds.value.has(conversationId)) return;
    const next = new Set(unreadConversationIds.value);
    next.delete(conversationId);
    unreadConversationIds.value = next;
  }

  function clearAllUnread() {
    if (unreadConversationIds.value.size === 0) return;
    unreadConversationIds.value = new Set();
  }

  function sendToResourceCenter() {
    window.open(VISION_AI_RESOURCE_CENTER_URL, '_blank');
  }

  async function createMockMessages({ isMockPrompt = false, intentTypeOverride } = {}) {
    // We want the fake summary intent type added to summaries tab, so we should grab any
    // summary intent
    const intentType =
      intentTypeOverride ?? (isMockPrompt ? SUMMARY_INTENT_TYPES[0] : currentIntentType.value);

    const mockedUserMessage = {
      id: 'mockedUserMessage',
      message: defaultPrompts.value[0]?.message,
      sender: VISION_AI_SENDER.USER,
      createdAt: dayjs(),
      isWarningMessage: true,
      intentType,
      intentQuery: {},
      conversationId: null,
    };
    visionAiMessagesStore.addMessageToChatWindow(mockedUserMessage);

    const warningMessage = {
      id: 'warning',
      message: getWarningMessage(currentIntentType.value),
      isWarningMessage: true,
      sender: VISION_AI_SENDER.ASSISTANT,
      createdAt: dayjs(),
      intentType,
      intentQuery: {},
      conversationId: null,
    };
    visionAiMessagesStore.addMessageToChatWindow(warningMessage);
  }

  async function handleClearChatHistory() {
    try {
      const response = await clearChatHistory();
      const trackingStore = useTrackingStore();
      trackingStore.track(VISION_AI_MIXPANEL_EVENTS.AI_WIDGET_INTERACTION, {
        interaction: VISION_AI_WIDGET_INTERACTIONS.DELETE_ALL_HISTORY,
        summaryCount: response?.data?.assistantMessagesDeleted ?? null,
      });
    } catch {
      addToastWithLife({
        message: 'Your chat history could not be deleted.',
        severity: 'error',
      });
    }
  }

  function onConversationCreatedNotification(conversationData) {
    visionAiConversationsStore.addConversationWebSocket(conversationData);
  }

  function onConversationsDeletedNotification(conversationsData) {
    visionAiConversationsStore.deleteConversationsWebSocket(conversationsData);
  }

  function onConversationUpdatedNotification(conversationData) {
    visionAiConversationsStore.updateConversationWebSocket(conversationData);
  }

  async function copyResponse(message) {
    try {
      let messageToCopy = message.replace(/(<\/?p>)/g, '');
      messageToCopy = messageToCopy.replace(/(<ul>)/g, '');
      messageToCopy = messageToCopy.replace(/(<\/ul>)/g, '\n');
      messageToCopy = messageToCopy.replace(/(<li>)/g, '- ');
      messageToCopy = messageToCopy.replace(/(<\/li>)/g, '');
      messageToCopy = messageToCopy.replace(/(<\/?(strong|i)>)/g, '');
      await navigator.clipboard.writeText(messageToCopy);
      addToastWithLife({
        message: 'Response copied',
        severity: 'success',
      });
    } catch {
      addPersistentToast({
        message: 'Failed to copy response. Please try again.',
        severity: 'error',
      });
    }
  }

  async function startConversation({
    message,
    buttonIdentifier,
    extra,
    intentTypeOverride,
    filters,
  }) {
    expectingNewMessage.value = true;

    const isMetricAnalyticsPrompt = DEFAULT_METRIC_AND_ANALYTICS_PROMPTS.includes(message);
    const defaultIntentType = INTENT_TYPES.UNIFIED;
    const intentType =
      intentTypeOverride ?? (isMetricAnalyticsPrompt ? defaultIntentType : currentIntentType.value);
    const sender = VISION_AI_SENDER.USER;
    const intentQuery = getIntentQuery(intentType);
    const actionType = isMetricAnalyticsPrompt ? ACTION_TYPES.QUESTION : null;
    let conversationId = visionAiConversationsStore.currentConversationId;

    if (!conversationId) {
      try {
        const conversation = await visionAiConversationsStore.createConversation();
        conversationId = conversation.id;
      } catch (error) {
        expectingNewMessage.value = false;
        throw error;
      }
    }

    if (JSON.stringify(intentQuery).includes('updates.0.data')) {
      logger.error(`Malformed intent_query sent for conversation id: ${conversationId}`);
    }

    const currentBrandId = identityStore.currentBrand?.id ?? 0;
    const requestContext = buildRequestContext(
      route,
      currentBrandId,
      filters ?? {},
      buttonIdentifier,
      extra,
    );

    const tempId = visionAiMessagesStore.addOptimisticUserMessage({
      conversationId,
      message,
      intentType,
    });
    expectingNewMessage.value = false;

    try {
      await visionAiMessagesStore.createMessage({
        conversationId,
        sender,
        message,
        intentType,
        intentQuery,
        actionType,
        usedPrompt: true,
        requestContext,
        optimisticId: tempId,
      });
    } catch (error) {
      visionAiMessagesStore.removeOptimisticUserMessage(tempId);
      notificationStore.setToast({
        message: 'Failed to send message. Please try again.',
        type: 'error',
      });
      throw error;
    }
  }

  async function handleSourceButtonClicked(intentType, sourceUrl) {
    if (intentType === INTENT_TYPES.DASHBOARD_REPORTS && route.name === DASHBOARD_ROUTE_NAMES.ID) {
      // if the new dashboard route.id is not equal to the current dashboard route.id
      if (sourceUrl.params.id.toString() !== route.params.id) {
        await dashboardsStore.setCurrentDashboard({ id: sourceUrl.params.id });
      }

      // if the new dashboard route.id is equal to the current dashboard route.id but the query is different
      const routeQuery = pick(route.query, Object.keys(sourceUrl.query));

      if (!isEqual(sourceUrl.query, routeQuery)) {
        const { startDate, endDate, contextStartDate, contextEndDate } = sourceUrl.query;
        await dashboardsStore.updateDateRanges({
          reportDateRange: [startDate, endDate],
          contextDateRange: [contextStartDate, contextEndDate],
        });
      }
    }

    if (
      (intentType === INTENT_TYPES.COMMENTS || intentType === INTENT_TYPES.DMS) &&
      route.name.includes(communityRouteName)
    ) {
      if (sourceUrl.params.brandLabel !== identityStore.currentBrandLabel) {
        currentSourceUrl.value = sourceUrl;
      }
    }
  }

  function clearCurrentSourceUrl() {
    currentSourceUrl.value = null;
  }

  function checkIfSamePageSource(sourceUrl) {
    const {
      isDashboardsRoute,
      isTrendsRoute,
      isTopicsRoute,
      isCommunityRoute,
      checkTopicSourceParamsEqual,
      checkTrendsSourceParamsEqual,
      checkCommunitySourceParamsEqual,
    } = useVisionAiSourceLinks();
    if (isDashboardsRoute.value) {
      if (sourceUrl?.query) {
        const routeQuery = pick(route.query, Object.keys(sourceUrl.query));
        return isEqual(sourceUrl.query, routeQuery);
      }
    }

    if (isTrendsRoute.value && sourceUrl?.query) {
      return checkTrendsSourceParamsEqual(sourceUrl);
    }

    if (isTopicsRoute.value && sourceUrl?.query) {
      return checkTopicSourceParamsEqual(sourceUrl);
    }

    if (isCommunityRoute.value) {
      return checkCommunitySourceParamsEqual(sourceUrl);
    }

    return false;
  }

  function startNewChat() {
    visionAiConversationsStore.setSummariesSelectedValue(false);
    visionAiConversationsStore.setCurrentConversationId(null);
    clearLocalChatHistory();
  }

  function initSocketListeners() {
    // Register socket events
    socketStore.addListener(
      VISION_AI_SOCKET_EVENTS.VISION_AI_MESSAGE_CREATED,
      visionAiMessagesStore.onMessageCreatedNotification,
    );
    socketStore.addListener(
      VISION_AI_SOCKET_EVENTS.VISION_AI_CLEAR_MESSAGES,
      visionAiMessagesStore.onClearMessagesNotification,
    );
    socketStore.addListener(
      VISION_AI_SOCKET_EVENTS.VISION_AI_CONVERSATION_CREATED,
      onConversationCreatedNotification,
    );
    socketStore.addListener(
      VISION_AI_SOCKET_EVENTS.VISION_AI_CONVERSATION_UPDATED,
      onConversationUpdatedNotification,
    );
    socketStore.addListener(
      VISION_AI_SOCKET_EVENTS.VISION_AI_CONVERSATIONS_DELETED,
      onConversationsDeletedNotification,
    );
  }

  // watchers
  watch(
    () => socketStore.id,
    (id) => {
      if (id) {
        initSocketListeners();
      }
    },
    { immediate: true },
  );

  watch(
    () => visionAiConversationsStore.conversationMessages,
    () => {
      visionAiMessagesStore.setMessages({
        newMessages: visionAiConversationsStore.conversationMessages,
      });
    },
  );

  watch(
    () => route?.name,
    () => {
      if (canAccessConversational.value) {
        clearLocalChatHistory();
        visionAiConversationsStore.resetToDefaultConversationState();
      }
    },
  );

  // Prune state tied to deleted conversations so stale ids can't keep the badge lit
  // or leave a phantom click pointer that 404s when the user clicks.
  watch(
    () => visionAiConversationsStore.conversations,
    () => {
      const hasUnread = unreadConversationIds.value.size > 0;
      const hasPointer = pendingCompletionConversationId.value !== null;
      if (!hasUnread && !hasPointer) return;

      const validIds = new Set(visionAiConversationsStore.conversations.map((c) => c.id));

      if (hasUnread) {
        const next = new Set();
        let changed = false;
        unreadConversationIds.value.forEach((id) => {
          if (validIds.has(id)) next.add(id);
          else changed = true;
        });
        if (changed) unreadConversationIds.value = next;
      }

      if (hasPointer && !validIds.has(pendingCompletionConversationId.value)) {
        clearPendingCompletion();
      }
    },
    { deep: true },
  );

  return {
    // states
    canAccessConversational,
    canAccessVisionAiButNotConversational,
    canAccessVisionAiTextbox,
    currentIntentType,
    currentSourceUrl,
    defaultPrompts,
    expectingNewMessage,
    forceHideVisionAi,
    hasPendingCompletion,
    hasUnreadConversations,
    isCommunityInsightsRoute,
    isLoadingPillExpanded,
    isPendingCompletionPillExpanded,
    isVisionChatPanelOpen,
    loadingMessageInCurrentConversation,
    messages,
    pending,
    pendingCompletionConversationId,
    popupIsExpanded,
    showConversationHistorySidebar,
    unreadConversationIds,
    sidePanelCannedPrompts,
    visionAiChatWindowOpen,

    // methods
    checkIfSamePageSource,
    clearAllUnread,
    clearChatHistory,
    clearCurrentSourceUrl,
    clearLoadingPill,
    clearLocalChatHistory,
    clearPendingCompletion,
    closeChatWindow,
    consumePendingCompletion,
    copyResponse,
    createMockMessages,
    expandChatPopup,
    handleClearChatHistory,
    handleSourceButtonClicked,
    hideConversationHistory,
    isConversationUnread,
    markConversationRead,
    markConversationUnread,
    onConversationCreatedNotification,
    onConversationsDeletedNotification,
    onConversationUpdatedNotification,
    openChatWindow,
    sendToResourceCenter,
    setVisionChatPanelOpen,
    shrinkChatPopup,
    startConversation,
    startNewChat,
    triggerLoadingPill,
    triggerPendingCompletion,
  };
});
